/**
 * State persistence for the prediction market learning platform.
 * Handles localStorage read/write with schema versioning and migration.
 */

import type { OrderBook, Position } from '../engine/types'
import type { SimulationSpeed } from '../engine/time'

// =============================================================================
// Constants
// =============================================================================

/** Current schema version - increment when making breaking changes */
export const CURRENT_VERSION = 1

/** LocalStorage key for persisted state */
export const STORAGE_KEY = 'prediction-market-learner'

// =============================================================================
// Types
// =============================================================================

/**
 * A simulation scenario that can be saved and loaded.
 */
export interface SimulationScenario {
  id: string
  name: string
  description: string
  market: {
    question: string
    description: string
  }
  initialOrderBook: {
    bids: Array<{ side: 'buy'; type: 'limit'; price: number; quantity: number }>
    asks: Array<{ side: 'sell'; type: 'limit'; price: number; quantity: number }>
  }
  userStartingBalance: number
}

/**
 * A saved snapshot of a simulation state.
 */
export interface SavedScenario {
  id: string
  name: string
  createdAt: number
  scenario: SimulationScenario
  snapshotState: {
    orderBook: OrderBook
    userBalance: number
    userPositions: Position[]
  }
}

/**
 * Visualization mode for UI complexity.
 */
export type VisualizationMode = 'beginner' | 'advanced'

/**
 * The complete persisted state structure.
 * This is what gets saved to localStorage.
 */
export interface PersistedState {
  /** Schema version for migrations */
  version: number
  /** User's learning progress */
  progress: {
    completedLessons: string[]
    currentLesson: string | null
    quizScores: Record<string, number>
    lastAccessed: number
  }
  /** User preferences */
  settings: {
    visualizationMode: VisualizationMode
    simulationSpeed: Exclude<SimulationSpeed, 'paused'>
    soundEnabled: boolean
  }
  /** Sandbox mode saved scenarios */
  sandbox: {
    savedScenarios: SavedScenario[]
  }
}

// =============================================================================
// Default State
// =============================================================================

/**
 * Get the default persisted state for new users.
 */
export function getDefaultState(): PersistedState {
  return {
    version: CURRENT_VERSION,
    progress: {
      completedLessons: [],
      currentLesson: null,
      quizScores: {},
      lastAccessed: Date.now(),
    },
    settings: {
      visualizationMode: 'beginner',
      simulationSpeed: 'normal',
      soundEnabled: true,
    },
    sandbox: {
      savedScenarios: [],
    },
  }
}

// =============================================================================
// Schema Migration
// =============================================================================

/**
 * Migrate stored state to the current schema version.
 * Handles missing/invalid data gracefully by falling back to defaults.
 *
 * @param stored - The raw value from localStorage (parsed JSON)
 * @returns A valid PersistedState at the current version
 */
export function migrateState(stored: unknown): PersistedState {
  // Handle null, undefined, or non-object values
  if (!stored || typeof stored !== 'object') {
    return getDefaultState()
  }

  const state = stored as Partial<PersistedState>

  // For now, just merge with defaults to fill in any missing fields
  // Future: Add migration logic here when schema evolves
  // const version = state.version ?? 0
  // if (version < 2) { /* migrate v1 to v2 */ }
  const defaults = getDefaultState()

  return {
    version: CURRENT_VERSION,
    progress: {
      ...defaults.progress,
      ...(state.progress ?? {}),
    },
    settings: {
      ...defaults.settings,
      ...(state.settings ?? {}),
    },
    sandbox: {
      ...defaults.sandbox,
      ...(state.sandbox ?? {}),
    },
  }
}

// =============================================================================
// LocalStorage Helpers
// =============================================================================

/**
 * Load persisted state from localStorage.
 * Returns migrated state or defaults if not found/invalid.
 */
export function loadPersistedState(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return getDefaultState()
    }

    const parsed = JSON.parse(raw)
    return migrateState(parsed)
  } catch (error) {
    console.error('Failed to load persisted state:', error)
    return getDefaultState()
  }
}

/**
 * Save persisted state to localStorage.
 *
 * @param state - The state to persist
 * @returns true if successful, false if storage failed
 */
export function savePersistedState(state: PersistedState): boolean {
  try {
    const serialized = JSON.stringify(state)
    localStorage.setItem(STORAGE_KEY, serialized)
    return true
  } catch (error) {
    console.error('Failed to save persisted state:', error)
    return false
  }
}

/**
 * Clear all persisted state (useful for testing or reset).
 */
export function clearPersistedState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error('Failed to clear persisted state:', error)
  }
}
